import { SessionCreateResponse, UserAgreementResponse, SurveySubmitResponse } from '../types/session';
import service from './index';

// 地区选项
export interface RegionOption {
    key: string;
    code: string;
    name: string;
}

// 地区列表响应
export interface RegionListResponse {
    regions: RegionOption[];
    default_key?: string;
    title?: string;
    subTitle?: string;
    otherNotSupportTips?: string;
    otherNotSupportLink?: string;
}

// 获取地区列表
export const getRegionList = async (): Promise<RegionListResponse> => {
    try {
        const response = await service.get<any, RegionListResponse>('/api/v1/get-region-list');
        return {
            regions: response?.regions ?? [],
            default_key: response?.default_key,
            title: response?.title ?? '',
            subTitle: response?.subTitle ?? '',
            otherNotSupportTips: response?.otherNotSupportTips ?? '',
            otherNotSupportLink: response?.otherNotSupportLink ?? ''
        };
    } catch (e) {
        console.error('获取地区列表失败:', e);
        return { regions: [], default_key: undefined, title: '', subTitle: '', otherNotSupportTips: '', otherNotSupportLink: '' };
    }
};

// 历史topic类型定义
export interface HistoryTopic {
    id: string;
    topic: string;
}

// 获取存储的会话ID
export const getStoredSessionId = (): string | null => {
    return localStorage.getItem('sessionId');
};

// 存储会话ID
export const setStoredSessionId = (sessionId: string): void => {
    localStorage.setItem('sessionId', sessionId);
};

// 清除存储的会话ID
export const clearStoredSessionId = (): void => {
    localStorage.removeItem('sessionId');
};

// 获取或生成 cookie_hash（首次访问时自动生成并持久化）
export const getStoredCookieHash = (): string => {
    let hash = localStorage.getItem('cookie_hash');
    if (!hash) {
        hash = (typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('')
        ).replace(/-/g, '');
        localStorage.setItem('cookie_hash', hash);
    }
    return hash;
};

// 存储 cookie_hash
export const setStoredCookieHash = (cookieHash: string): void => {
    localStorage.setItem('cookie_hash', cookieHash);
};

// 清除存储的 cookie_hash
export const clearStoredCookieHash = (): void => {
    localStorage.removeItem('cookie_hash');
};

// 获取dataParam，优先URL，没有则localStorage
export function getStoredDataParam(): string | null {
    const searchParams = new URLSearchParams(window.location.search);
    let dataParam = searchParams.get('SN');
    if (!dataParam) {
        dataParam = localStorage.getItem('url_data_param');
    }
    if (!dataParam || dataParam.trim() === '') {
        localStorage.removeItem('url_data_param');
        return null;
    }
    return dataParam;
}

// 将所有 URL 参数存储到 localStorage（JSON 字符串）
export function setStoredUrlParams(params: Record<string, string>): void {
    localStorage.setItem('url_params_json', JSON.stringify(params));
}

// 用户选择的国家持久化
export function setStoredCountry(code: string): void {
    localStorage.setItem('selected_country', code);
}

export function getStoredCountry(): string | null {
    return localStorage.getItem('selected_country');
}

// 获取所有 URL 参数，优先从当前 URL 解析，没有则从 localStorage 回读
export function getUrlParamsAsObject(): Record<string, string> | null {
    const searchParams = new URLSearchParams(window.location.search);
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
        params[key] = value;
    });

    if (Object.keys(params).length > 0) {
        // URL 有参数时同步更新 localStorage，避免旧缓存干扰
        setStoredUrlParams(params);
        if (params.SN) {
            localStorage.setItem('url_data_param', params.SN);
        }
        return params;
    }

    // URL 无参数时回读缓存，保留 clientInfo 等数据供后续请求使用
    const cached = localStorage.getItem('url_params_json');
    if (cached) {
        try {
            return JSON.parse(cached);
        } catch {
            // ignore invalid JSON
        }
    }
    return null;
}

const decodeBase64Param = (value: string): string | null => {
    try {
        if (typeof atob === 'function') {
            return atob(value);
        }
        return null;
    } catch {
        return null;
    }
};

// 解析 base64 编码的 data 参数，支持 JSON 对象和 query string 两种格式
export function parseDataParam(data: string): Record<string, string> | null {
    const decoded = decodeBase64Param(data);
    if (!decoded) return null;

    // 优先尝试 JSON 解析
    try {
        const obj = JSON.parse(decoded);
        if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
            const result: Record<string, string> = {};
            for (const [ k, v ] of Object.entries(obj)) {
                result[k] = typeof v === 'string' ? v : String(v);
            }
            return result;
        }
    } catch {
        // 解析失败，非 JSON 格式
    }

    // 降级为 query string 解析 (A=a&B=b)
    try {
        const params = new URLSearchParams(decoded);
        const result: Record<string, string> = {};
        params.forEach((value, key) => { result[key] = value; });
        return Object.keys(result).length > 0 ? result : null;
    } catch {
        // 解析失败，非 query string 格式
    }

    return null;
}

const extractSerialNumber = (payload: any): string | null => {
    if (!payload || typeof payload !== 'object') return null;

    const fromArray = Array.isArray(payload.SerialNumbers)
        ? payload.SerialNumbers.find((item: unknown) => typeof item === 'string' && item.trim().length > 0)
        : null;
    if (fromArray) {
        return fromArray.trim();
    }

    if (typeof payload.SerialNumber === 'string' && payload.SerialNumber.trim().length > 0) {
        return payload.SerialNumber.trim();
    }

    return null;
};

export function getStoredSerialNumber(): string | null {
    const rawParam = getStoredDataParam();
    if (!rawParam) return null;
    const decoded = decodeBase64Param(rawParam);
    if (!decoded) return null;
    try {
        const payload = JSON.parse(decoded);
        return extractSerialNumber(payload);
    } catch {
        return null;
    }
}

// 创建新会话
export const createSession = async (
    params?: { force_new?: boolean; data?: string; clientInfo?: Record<string, string>; country?: string }
): Promise<SessionCreateResponse> => {
    const forceNew = params?.force_new ?? false;
    const data = params?.data;
    const clientInfo = params?.clientInfo;
    const country = params?.country;

    try {
        const requestData: { force_new: boolean; client_info?: Record<string, string> | string; country?: string; source_sn?: string } = {
            force_new: forceNew
        };

        // 优先使用 clientInfo（JSON 对象直接透传），否则降级用旧的 data 字符串
        if (clientInfo && Object.keys(clientInfo).length > 0) {
            requestData.client_info = clientInfo;
        } else if (data) {
            requestData.client_info = data;
        }

        if (country) {
            requestData.country = country;
        }

        // 从 URL 取 SN 作为 sourceSN，用于后端判断是否继承 session
        const sourceSN = new URLSearchParams(window.location.search).get('SN');
        if (sourceSN) {
            requestData.source_sn = sourceSN;
        }

        const response = await service.post<any, SessionCreateResponse>('/api/v1/session/create', requestData);

        // 存储新的会话ID
        setStoredSessionId(response.session_id);

        return response;
    } catch (error) {
        console.error('创建会话失败:', error);
        // 模拟API响应（开发环境）
        const mockSessionId = `session_${Date.now()}`;
        setStoredSessionId(mockSessionId);

        return {
            session_id: mockSessionId,
            is_restored: false,
            chat_history: [],
            is_open: false
        };
    }
};

// 获取用户协议内容
export const getUserAgreement = async (): Promise<UserAgreementResponse> => {
    try {
        const response = await service.get<any, UserAgreementResponse>('/api/v1/get-user-notice');
        // 容错：后端字段缺失时返回空字符串，文案完全由后端维护
        return {
            introduction: response?.introduction ?? '',
            button_content: response?.button_content ?? '',
            confirm_select_content: response?.confirm_select_content ?? '',
            refuse_button_content: response?.refuse_button_content ?? '',
            refuse_button_url: response?.refuse_button_url ?? ''
        };
    } catch (e) {
        // 失败时返回空字符串，保持弹窗渲染不报错
        return {
            introduction: '',
            button_content: '',
            confirm_select_content: '',
            refuse_button_content: '',
            refuse_button_url: ''
        };
    }
};

// 提交调查表单
export const submitSurvey = async (
    params: { rating: number; resolvedOnFirstAttempt: boolean; comment?: string; session_id: string }
): Promise<SurveySubmitResponse> => {
    try {
        const response = await service.post<any, SurveySubmitResponse>('/api/v1/survey-submit', params);
        return response;
    } catch (e) {
        console.error('提交调查表单失败:', e);
        return { success: false };
    }
};

export interface RecommendQuestionsResponse {
    greetingText?: string;
    greetingQuestion?: string[];
}

export const getHomeQuestions = async (): Promise<RecommendQuestionsResponse> => {
    try {
        const response = await service.get<any, RecommendQuestionsResponse>('/api/v1/get-recommend-questions');
        return response;
    } catch (e) {
        console.error('获取推荐问题失败:', e);
        return {};
    }
};

// 获取历史topic列表
export const getHistoryTopics = async (dataParam: string): Promise<HistoryTopic[]> => {
    const sessionId = getStoredSessionId();
    if (!sessionId) {
        console.warn('获取历史topic失败: session_id 不存在');
        return [];
    }
    const params = {
        session_id: sessionId,
        data: dataParam,
        serial_number_list: []
    };

    try {
        const response = await service.post<any, HistoryTopic[]>('/api/v1/get-history-topics', params);
        return response ?? [];
    } catch (e) {
        console.error('获取历史topic失败:', e);
        return [];
    }
};

// 获取历史topic对应的summary内容
export const getHistoryTopicSummary = async (topicId: string, topicText: string): Promise<string> => {
    try {
        const response = await service.post<any, { summary: string }>('/api/v1/get-history-topic-summary', {
            session_id: getStoredSessionId(),
            topic_id: topicId
        });
        return response?.summary ?? '';
    } catch (e) {
        console.error('获取历史topic对应的summary内容失败:', e);
        // 返回模拟数据用于测试
        return `### ${topicText}\n\n Error.`;
    }
};

export interface TimeoutStrategyResponse {
    message?: string;
    button?: boolean;
}

// 获取超时策略询问话术
export const getTimeoutStrategy = async (): Promise<TimeoutStrategyResponse> => {
    try {
        const response = await service.post<any, TimeoutStrategyResponse>('/api/v1/timeouts/first-reminder', {
            session_id: getStoredSessionId()
        });
        return {
            message: response?.message ?? '',
            button: !!response?.button
        };
    } catch (e) {
        console.error('获取超时策略询问话术失败:', e);
        return { message: '', button: false };
    }
};

// 获取超时策略关闭话术
export const getTimeoutCloseStrategy = async (): Promise<string> => {
    try {
        const response = await service.get<any, { message: string }>('/api/v1/timeouts/closing-notice');
        return response?.message ?? '';
    } catch (e) {
        console.error('获取超时策略关闭话术失败:', e);
        return '';
    }
};

// 用户选择已解决
export const userSelectedResolved = async (): Promise<boolean> => {
    try {
        const response = await service.post<any, { success: boolean }>('/api/v1/timeouts/put-resolved', {
            session_id: getStoredSessionId()
        });
        const success = !!response?.success;
        console.log('userSelectedResolved result:', success);
        return success;
    } catch (e) {
        console.log('userSelectedResolved error:', e);
        return false;
    }
};

// 关闭会话
export const closeSession = async (): Promise<boolean> => {
    try {
        const response = await service.post<any, { success: boolean }>('/api/v1/session/close-and-summarize', {
            session_id: getStoredSessionId()
        });
        return response?.success ?? false;
    } catch (e) {
        console.error('关闭会话失败:', e);
        return false;
    }
};

// 用户协议是否接受
export const reportUserAgreementConsent = async (
    params: { accepted: boolean; serial_number_base64?: string | null }
): Promise<void> => {
    try {
        await service.post('/api/v1/user-notice/consent', {
            accepted: params.accepted,
            serial_number_base64: params.serial_number_base64 || undefined
        });
    } catch (e) {
        console.error('用户协议反馈失败:', e);
    }
};

// 记录用户点击购买按钮（fire-and-forget）
export const billableClick = (sessionId: string): void => {
    service.post('/api/v1/billable/click', { session_id: sessionId })
        .catch((e) => {
            console.error('billable/click 上报失败:', e);
        });
};

// 记录用户完成购买（fire-and-forget）
export const billableFinalize = (sessionId: string): void => {
    service.post('/api/v1/billable/finalize', { session_id: sessionId })
        .catch((e) => {
            console.error('billable/finalize 上报失败:', e);
        });
};